import * as yup from 'yup';
import { i18n } from '@/plugins/i18n';

import { Constants } from '@/constants';
import type { Locales, DotDto } from '@/services/types/api.types';

const { t } = i18n.global;

export const dotVotingChoiceValidationSchema = (languageEnabled: Locales[]) =>
  yup.object<DotDto>({
    answers: yup.object().shape({
      steps: yup.array().of(
        yup.object().shape({
          dot_voting_type: yup
            .number()
            .typeError(() =>
              t('validation.invalid_number', { field: t('validation.forms.dot-voting.type.field-name') }),
            )
            .when('$type', {
              is: (val: number) => val === Constants.Form.Type.DotVoting,
              then: (schema) => schema.required('validation.forms.dot-voting.type.required'),
              otherwise: (schema) => schema.optional(),
            }),
          dot_voting_measurement_type: yup
            .number()
            .typeError(() =>
              t('validation.invalid_number', { field: t('validation.forms.dot-voting.measurement-type.field-name') }),
            )
            .when('$type', {
              is: (val: number) => val === Constants.Form.Type.DotVoting,
              then: (schema) => schema.required('validation.forms.dot-voting.measurement-type.required'),
              otherwise: (schema) => schema.optional(),
            }),
          default_cost_per_choice: yup
            .number()
            .integer(
              t('validation.invalid_integer', {
                field: t('validation.forms.dot-voting.default_cost_per_choice.field-name'),
              }),
            )
            .typeError(() =>
              t('validation.invalid_number', {
                field: t('validation.forms.dot-voting.default_cost_per_choice.field-name'),
              }),
            )
            .when('$type', {
              is: (val: number) => val === Constants.Form.Type.DotVoting,
              then: (schema) =>
                schema
                  .required(
                    t('validation.required', {
                      field: t('validation.forms.dot-voting.default_cost_per_choice.field-name'),
                    }),
                  )
                  .min(
                    1,
                    t('validation.min_value', {
                      field: t('validation.forms.dot-voting.default_cost_per_choice.field-name'),
                      min: 1,
                    }),
                  )
                  .test(
                    'less-than-budget',
                    t('validation.forms.dot-voting.default_cost_per_choice.less-than-budget'),
                    function (value) {
                      const { total_budget } = this.parent;
                      if (value && total_budget && value > total_budget) {
                        return false;
                      }
                      return true;
                    },
                  ),
              otherwise: (schema) => schema.optional(),
            }),
          total_budget: yup
            .mixed()
            .transform((value, originalValue) => {
              if (originalValue === '' || originalValue === null || originalValue === undefined) {
                return undefined;
              }
              const num = Number(originalValue);
              return Number.isNaN(num) ? originalValue : num;
            })
            .test('type-check', function (value) {
              const { dot_voting_measurement_type } = this.parent;
              const fieldName =
                dot_voting_measurement_type === Constants.Form.DotVoting.MeasurementType.Money
                  ? t('validation.forms.dot-voting.total-budget.money.field-name')
                  : t('validation.forms.dot-voting.total-budget.points.field-name');

              if (typeof value !== 'number' && value !== undefined) {
                return this.createError({
                  message: t('validation.invalid_number', { field: fieldName }),
                });
              }
              return true;
            })
            .test('integer-message', function (value) {
              const { dot_voting_measurement_type } = this.parent;
              const fieldName =
                dot_voting_measurement_type === Constants.Form.DotVoting.MeasurementType.Money
                  ? t('validation.forms.dot-voting.total-budget.money.field-name')
                  : t('validation.forms.dot-voting.total-budget.points.field-name');

              if (value !== undefined && !Number.isInteger(value)) {
                return this.createError({
                  message: t('validation.invalid_integer', { field: fieldName }),
                });
              }
              return true;
            })
            .when('$type', {
              is: (val: number) => val === Constants.Form.Type.DotVoting,
              then: (schema) =>
                schema
                  .test('required-message', function (value) {
                    const { dot_voting_measurement_type } = this.parent;
                    const fieldName =
                      dot_voting_measurement_type === Constants.Form.DotVoting.MeasurementType.Money
                        ? t('validation.forms.dot-voting.total-budget.money.field-name')
                        : t('validation.forms.dot-voting.total-budget.points.field-name');

                    if (!value) {
                      return this.createError({
                        message: t('validation.required', { field: fieldName }),
                      });
                    }
                    return true;
                  })
                  .test('min-value-message', function (value) {
                    const { dot_voting_measurement_type } = this.parent;
                    const fieldName =
                      dot_voting_measurement_type === Constants.Form.DotVoting.MeasurementType.Money
                        ? t('validation.forms.dot-voting.total-budget.money.field-name')
                        : t('validation.forms.dot-voting.total-budget.points.field-name');

                    if (value !== undefined && value < 1) {
                      return this.createError({
                        message: t('validation.min_value', { field: fieldName, min: 1 }),
                      });
                    }
                    return true;
                  }),
              otherwise: (schema) => schema.optional(),
            }),
          number_of_steps: yup
            .number()
            .typeError(() =>
              t('validation.invalid_number', { field: t('validation.forms.dot-voting.number_of_steps.field-name') }),
            )
            .when(['$type', 'dot_voting_type'], {
              is: (formType: number, dotVotingType: number) =>
                formType === Constants.Form.Type.DotVoting && dotVotingType === Constants.Form.DotVoting.Type.Variable,
              then: (schema) =>
                schema
                  .required(
                    t('validation.required', { field: t('validation.forms.dot-voting.number_of_steps.field-name') }),
                  )
                  .min(
                    3,
                    t('validation.min_value', {
                      field: t('validation.forms.dot-voting.number_of_steps.field-name'),
                      min: 3,
                    }),
                  )
                  .max(
                    10,
                    t('validation.max_value', {
                      field: t('validation.forms.dot-voting.number_of_steps.field-name'),
                      max: 10,
                    }),
                  ),
              otherwise: (schema) => schema.optional().nullable(),
            }),
        }),
      ),
    }),
  });
